import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static get targets() {
    return ["chart"]
  }

  connect() {
    this.charts = this.chartTargets.map((element) => {
      const chart = echarts.init(element)
      const config = JSON.parse(element.dataset.chart)
      chart.setOption(this.optionsFor(config), false)
      return chart
    })

    this.resizeHandler = () => this.resize()
    window.addEventListener("resize", this.resizeHandler)
    setTimeout(this.resizeHandler, 100)
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeHandler)
    this.charts.forEach((chart) => chart.dispose())
  }

  resize() {
    this.charts.forEach((chart) => chart.resize())
  }

  optionsFor(config) {
    if (config.type === "pie") return this.pieOptions(config)
    if (config.type === "calendar") return this.calendarOptions(config)

    const textColor = this.chartTextColor
    const axisColor = this.chartAxisColor
    const series = config.series.map((item, index) => ({
      name: item.name,
      type: config.type,
      data: item.data,
      smooth: config.type === "line",
      symbolSize: 7,
      yAxisIndex: item.axis || 0,
      itemStyle: { color: this.colors[index % this.colors.length] }
    }))
    const hasSecondAxis = config.series.some((item) => item.axis === 1)

    return {
      color: this.colors,
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      legend: { top: 14, textStyle: { color: textColor } },
      toolbox: { right: 18, iconStyle: { borderColor: textColor }, feature: { saveAsImage: { pixelRatio: 2 } } },
      grid: { left: 72, right: hasSecondAxis ? 72 : 32, top: 62, bottom: config.gridBottom || 90 },
      xAxis: {
        type: "category",
        data: config.labels,
        axisLabel: { interval: 0, rotate: config.labels.length > 8 ? 35 : 0, color: textColor },
        axisLine: { lineStyle: { color: axisColor } }
      },
      yAxis: hasSecondAxis ? [this.valueAxis(config.series[0].name), this.valueAxis(config.series.find((item) => item.axis === 1).name)] : this.valueAxis(),
      series: series
    }
  }

  pieOptions(config) {
    const textColor = this.chartTextColor

    return {
      color: this.colors,
      tooltip: { trigger: "item", formatter: "{b}<br/>{a}: {c} ({d}%)" },
      legend: { type: "scroll", bottom: 10, textStyle: { color: textColor } },
      toolbox: { right: 18, iconStyle: { borderColor: textColor }, feature: { saveAsImage: { pixelRatio: 2 } } },
      series: [{
        name: config.value_name,
        type: "pie",
        radius: ["42%", "70%"],
        center: ["50%", "44%"],
        data: config.labels.map((label, index) => ({ name: label, value: config.values[index] })),
        label: { color: textColor, formatter: "{b}\n{d}%" }
      }]
    }
  }

  calendarOptions(config) {
    const textColor = this.chartTextColor
    const axisColor = this.chartAxisColor

    return {
      tooltip: {
        formatter: (params) => `${params.value[0]}<br/>${config.value_name}: ${params.value[1].toLocaleString()}`
      },
      visualMap: {
        min: 0,
        max: config.max,
        calculable: true,
        orient: "horizontal",
        left: "center",
        top: 8,
        text: ["多", "少"],
        textStyle: { color: textColor },
        inRange: { color: ["#edf2f7", "#b7d3e8", "#4f7cac", "#243f70"] }
      },
      calendar: {
        top: 68,
        left: 54,
        right: 28,
        bottom: 22,
        range: [config.start, config.end],
        cellSize: ["auto", 20],
        splitLine: { lineStyle: { color: axisColor } },
        itemStyle: { borderColor: this.chartGridColor, borderWidth: 2 },
        yearLabel: { show: false },
        monthLabel: { color: textColor, nameMap: "cn" },
        dayLabel: { color: textColor, firstDay: 1, nameMap: ["日", "一", "二", "三", "四", "五", "六"] }
      },
      series: [{
        name: config.value_name,
        type: "heatmap",
        coordinateSystem: "calendar",
        data: config.data
      }]
    }
  }

  valueAxis(name) {
    return {
      type: "value",
      name: name,
      axisLabel: { color: this.chartTextColor },
      nameTextStyle: { color: this.chartTextColor },
      splitLine: { lineStyle: { color: this.chartGridColor } }
    }
  }

  get chartTextColor() {
    return document.body.classList.contains("dark-theme") ? "#cbd5e1" : "#5d6673"
  }

  get chartAxisColor() {
    return document.body.classList.contains("dark-theme") ? "#94a3b8" : "#d9dee7"
  }

  get chartGridColor() {
    return document.body.classList.contains("dark-theme") ? "#64748b" : "#d9dee7"
  }

  get colors() {
    return ["#345995", "#e67e22", "#2a9d8f", "#7b5ea7", "#d1495b", "#4f7cac", "#efb366", "#66999b", "#9c6644", "#577590"]
  }
}
